import React, { createContext, useContext, useState, useCallback } from 'react';
import ApiService from '../services/api';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false); // Changed from true to false
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Fetch all products
  const fetchProducts = useCallback(async (force = false) => {
    if (hasLoaded && !force) return; // Don't fetch if already loaded unless forced
    
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getProducts();
      setProducts(data);
      setHasLoaded(true);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [hasLoaded]);

  // Force refresh products
  const refreshProducts = useCallback(async () => {
    await fetchProducts(true);
  }, [fetchProducts]);

  // Fetch single product by ID
  const fetchProductById = async (id) => {
    try {
      setError(null);
      const data = await ApiService.getProductById(id);
      return data;
    } catch (error) {
      setError(error.message);
      console.error('Error fetching product:', error);
      throw error;
    }
  };

  // Get products by category
  const getProductsByCategory = (categoryId) => {
    if (!categoryId) return products;
    return products.filter(product => product.category === categoryId);
  };

  // Get unique categories from products
  const getCategories = () => {
    const categories = products.reduce((acc, product) => {
      if (product.category && !acc.find(cat => cat.id === product.category)) {
        acc.push({
          id: product.category,
          name: product.categoryName || product.category,
          description: product.categoryDescription || ''
        });
      }
      return acc;
    }, []);
    return categories;
  };

  // Removed automatic fetching on mount - now products are loaded only when needed

  const value = {
    products,
    loading,
    error,
    fetchProducts,
    refreshProducts,
    fetchProductById,
    getProductsByCategory,
    getCategories,
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}; 